import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
// hooks
import { useParentRiskId } from 'hooks/risks/useParentRiskId'
// types
import { RootState } from 'store/store'
// utils
import sortBy from 'lodash/sortBy'

export const useRiskRelatedForms = () => {
  const router = useRouter()

  const { risk } = React.useContext(RiskAPIContext)

  // The parent's set is keyed by the parent record, the record's own set by the record itself.
  const parentRiskId = useParentRiskId(risk)

  const parentSet = useSelector((state: RootState) => state.relatedForms[parentRiskId] || [])
  const ownSet = useSelector((state: RootState) => state.relatedForms[risk?.id] || [])

  const parentForms = React.useMemo(
    () => sortBy(parentSet, 'position').map((form) => ({ ...form, ownerId: parentRiskId })),
    [parentSet, parentRiskId]
  )

  const ownForms = React.useMemo(
    () => sortBy(ownSet, 'position').map((form) => ({ ...form, ownerId: risk?.id })),
    [ownSet, risk?.id]
  )

  // What the tab strip renders: both sets side by side, which is what makes telling a linked form
  // from a related one unnecessary. Duplicates are kept — the same template can be reachable through
  // either owner, and the two tabs lead to different records.
  const combinedForms = React.useMemo(() => [...parentForms, ...ownForms], [parentForms, ownForms])

  // Set only when this record was created as a related form of its parent — that is exactly when
  // the parent's set contains its template. Replaces the old `router.query.parentRiskId` checks.
  const parentRelatedForm = parentForms.find(({ riskTemplateId }) => riskTemplateId === router.query.templateId)

  // The set this record belongs to — its parent's if it was created as a related form, its own
  // otherwise. This is "the record's related forms" in the sense the app had before the strip
  // started showing two sets at once, and what the places needing a single set still want.
  const relatedForms = parentRelatedForm ? parentForms : ownForms

  return { combinedForms, parentForms, ownForms, relatedForms, parentRelatedForm }
}
